export interface Fix {
  lat: number
  lon: number
  accuracyM: number
  at: number
}

export type LocatorStatus =
  | 'idle'
  | 'unsupported'
  | 'prompting'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  | 'tracking'

export interface LocatorEvents {
  onStatus: (status: LocatorStatus, detail?: string) => void
  onFix: (fix: Fix) => void
}

export interface Locator {
  start: () => void
  stop: () => void
  status: () => LocatorStatus
  last: () => Fix | null
}

/**
 * Wraps `watchPosition` so the interface can stay declarative about status.
 * High accuracy is requested because arrival radii are as tight as 150 m, and
 * that is also the expensive setting — so the caller is expected to stop the
 * watch whenever the companion is not the active view.
 */
export function createLocator({ onStatus, onFix }: LocatorEvents): Locator {
  let watchId: number | null = null
  let status: LocatorStatus = 'idle'
  let lastFix: Fix | null = null

  const setStatus = (next: LocatorStatus, detail?: string) => {
    if (status === next) return
    status = next
    onStatus(next, detail)
  }

  const start = () => {
    if (watchId !== null) return
    if (!('geolocation' in navigator)) {
      setStatus('unsupported')
      return
    }
    setStatus('prompting')
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const fix: Fix = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracyM: position.coords.accuracy,
          at: position.timestamp,
        }
        lastFix = fix
        setStatus('tracking')
        onFix(fix)
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) setStatus('denied')
        else if (error.code === error.POSITION_UNAVAILABLE) setStatus('unavailable')
        else setStatus('timeout')
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 30_000 },
    )
  }

  const stop = () => {
    if (watchId === null) return
    navigator.geolocation.clearWatch(watchId)
    watchId = null
    setStatus('idle')
  }

  return { start, stop, status: () => status, last: () => lastFix }
}
